class AccessoriesHub extends HTMLElement {
  connectedCallback() {
    if (this._bound) return;
    this._bound = true;

    this.addEventListener('click', this.onClick.bind(this));
    this.addEventListener('toggle', this.onToggle.bind(this), true);

    this.menuDetails = this.closest('details');

    if (this.classList.contains('acc-hub--desktop')) {
      this.querySelectorAll('[data-acc-hub-tab]').forEach((tab) => {
        tab.addEventListener('mouseenter', () => this.activateTab(tab));
      });

      const loadActive = () => this.loadImages(this.activePanel());
      this.closest('header-menu')?.addEventListener('mouseenter', loadActive);
      if (this.menuDetails) {
        this.menuDetails.addEventListener('toggle', () => {
          if (this.menuDetails.open) loadActive();
        });
      }

      const warmFirst = () => this.loadImages(this.activePanel());
      if ('requestIdleCallback' in window) {
        requestIdleCallback(warmFirst, { timeout: 2500 });
      } else {
        setTimeout(warmFirst, 1200);
      }
    } else if (this.menuDetails) {
      this.menuDetails.addEventListener('toggle', () => {
        if (this.menuDetails.open) this.loadImages(this);
      });
    }
  }

  onClick(event) {
    const moreBtn = event.target.closest('[data-acc-hub-view-more]');
    if (moreBtn && this.contains(moreBtn)) {
      event.preventDefault();
      this.toggleViewMore(moreBtn);
      return;
    }

    const tab = event.target.closest('[data-acc-hub-tab]');
    if (!tab || !this.contains(tab)) return;
    event.preventDefault();
    this.activateTab(tab);
  }

  toggleViewMore(btn) {
    const block = btn.closest('[data-acc-hub-series-block]');
    if (!block) return;

    const expanded = block.classList.toggle('is-expanded');
    btn.setAttribute('aria-expanded', expanded ? 'true' : 'false');

    const label = btn.querySelector('[data-acc-hub-view-more-label]');
    if (label) label.textContent = expanded ? 'View less' : 'View More';

    if (expanded) this.loadImages(block);
  }

  activateTab(tab) {
    if (!tab || !this.contains(tab)) return;
    const id = tab.getAttribute('data-acc-hub-tab');
    if (!id) return;

    this.querySelectorAll('[data-acc-hub-tab]').forEach((item) => {
      const on = item === tab;
      item.classList.toggle('is-active', on);
      item.setAttribute('aria-pressed', on ? 'true' : 'false');
    });

    this.querySelectorAll('[data-acc-hub-panel]').forEach((panel) => {
      const on = panel.getAttribute('data-acc-hub-panel') === id;
      panel.classList.toggle('is-active', on);
      if (on) {
        panel.removeAttribute('hidden');
        this.loadImages(panel);
      } else {
        panel.setAttribute('hidden', '');
      }
    });
  }

  onToggle(event) {
    const details = event.target;
    if (details.matches && details.matches('details[data-acc-hub-series]') && details.open) {
      this.loadImages(details);
    }
  }

  activePanel() {
    return (
      this.querySelector('[data-acc-hub-panel].is-active') ||
      this.querySelector('[data-acc-hub-panel]')
    );
  }

  loadImages(root) {
    if (!root) return;
    root.querySelectorAll('.acc-hub__card').forEach((card) => this.fetchCard(card));
  }

  cardHandle(card) {
    let handle = card.getAttribute('data-acc-hub-handle');
    if (!handle) {
      const href = card.getAttribute('href') || '';
      const match = href.match(/\/products\/([^/?#]+)/i);
      if (match) handle = decodeURIComponent(match[1]);
    }
    return handle || '';
  }

  cacheGet(handle) {
    try {
      return sessionStorage.getItem(`acc-hub-img:v3:${handle}`) || '';
    } catch (error) {
      return '';
    }
  }

  cacheSet(handle, src) {
    try {
      sessionStorage.setItem(`acc-hub-img:v3:${handle}`, src);
    } catch (error) {
      /* ignore quota / private mode */
    }
  }

  sizedImage(src, width) {
    if (!src) return '';
    const withProtocol = src.indexOf('//') === 0 ? `https:${src}` : src;
    if (/_\d+x\.(?:jpe?g|png|webp|gif)/i.test(withProtocol)) return withProtocol;
    return withProtocol.replace(/(\.(?:jpe?g|png|webp|gif|heic))(\?|$)/i, `_${width}x$1$2`);
  }

  applyImage(card, src, alt) {
    const img = card.querySelector('[data-acc-hub-img]');
    if (!img || !src) return;
    const url = this.sizedImage(src, 240);
    img.decoding = 'async';
    img.loading = 'eager';
    img.addEventListener(
      'load',
      () => {
        card.classList.add('has-image');
      },
      { once: true }
    );
    img.src = url;
    if (alt) img.alt = alt;
    if (img.complete && img.naturalWidth) card.classList.add('has-image');
  }

  fetchCard(card) {
    if (!card || card.classList.contains('has-image') || card.dataset.loading) return;

    const handle = this.cardHandle(card);
    if (!handle) return;

    const cached = this.cacheGet(handle);
    if (cached) {
      this.applyImage(card, cached);
      return;
    }

    card.dataset.loading = '1';
    fetch(`/products/${encodeURIComponent(handle)}.js`, { cache: 'no-store' })
      .then((response) => (response.ok ? response.json() : null))
      .then((product) => {
        if (!product) {
          delete card.dataset.loading;
          return;
        }
        const src = this.pickProductImage(product);
        if (!src) {
          delete card.dataset.loading;
          return;
        }
        this.cacheSet(handle, src);
        this.applyImage(card, src, product.title);
      })
      .catch(() => {
        delete card.dataset.loading;
      });
  }

  pickProductImage(product) {
    const urls = [];
    const push = (value) => {
      if (!value) return;
      const src = typeof value === 'string' ? value : value.src || value.url || '';
      if (!src) return;
      const normalized = src.indexOf('//') === 0 ? `https:${src}` : src;
      if (!urls.includes(normalized)) urls.push(normalized);
    };

    if (Array.isArray(product.images) && product.images.length) {
      product.images.forEach(push);
    } else if (Array.isArray(product.media)) {
      product.media.forEach((item) => {
        if (!item || item.media_type === 'video' || item.media_type === 'external_video') return;
        push(item.src);
      });
    } else {
      push(product.featured_image);
    }

    return urls[1] || urls[0] || '';
  }
}

if (!customElements.get('accessories-hub')) {
  customElements.define('accessories-hub', AccessoriesHub);
}
